import React, { useState, useEffect } from 'react';
import {
  Button,
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';
import { toast } from 'react-toastify';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';

import DefaultModal from 'components/Modal/DefaultModal';
import Pagination from 'components/Pagination';
import ReactButton from 'components/Buttons/ReactButton';
import ReactLoader from 'components/Loader';
import ModalConfirmation from 'components/Modal/ConfirmationModal';
import {
  icon_column_style,
  individual_column_style,
  individual_header_style,
  individual_icon_header_style
} from 'assets/styles/table_custom_style';
import IgnoredWordsService from '../../../service';
import { DEFAULT_PAGE_SIZE } from './hooks/useFetch';
import { useSearch } from './hooks/useSearch';
import { useSelect } from './hooks/useSelect';
import AddWordsModal from '../AddWordsModal';

const useFetch = (ignoredWordsId, search, activePage) => {
  const [data, setData] = useState([]);
  const [quantity, setQuantity] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const refetch = async () => {
    if (!ignoredWordsId) return;
    setLoading(true);
    try {
      const response = await IgnoredWordsService.getById(ignoredWordsId);
      const ignoredWordsData = response.data.data || response.data || {};
      const wordsList =
        ignoredWordsData.words.map((word) => word.original) || [];

      const filteredWords = wordsList.filter((word) =>
        word.toLowerCase().includes(search.toLowerCase())
      );

      const start = (activePage - 1) * DEFAULT_PAGE_SIZE;
      const paginatedWords = filteredWords.slice(
        start,
        start + DEFAULT_PAGE_SIZE
      );

      setData(
        paginatedWords.map((word, index) => ({ id: `${index}-${word}`, word }))
      );
      setQuantity(filteredWords.length);
    } catch (err) {
      setError(err);
      setErrorMessage(
        err.response?.data?.message || 'Erro ao carregar palavras ignoradas'
      );
      if (err.response?.status === 404) {
        setData([]);
        setQuantity(0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, [ignoredWordsId, search, activePage]);

  return { data, quantity, loading, error, errorMessage, refetch };
};

const ListModal = ({ isModalOpen, onModalToggle, ignoredWords, ...props }) => {
  const [activePage, setActivePage] = useState(1);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isAddWordsModalOpen, setIsAddWordsModalOpen] = useState(false);
  const [resourceId, setResourceId] = useState([]);
  const { search, debouncedSearch } = useSearch();
  const { data, quantity, loading, error, errorMessage, refetch } = useFetch(
    ignoredWords,
    search,
    activePage
  );
  const {
    state: { selectedRows, isAllRowsSelected, isAllItemsSelected },
    select,
    selectAllRows,
    selectAllItems,
    unselectAllItems
  } = useSelect();

  useEffect(() => {
    if (!isModalOpen) {
      setActivePage(1);
      unselectAllItems();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (!error || !errorMessage) return;
    onModalToggle(false);
    toast.error(errorMessage, {
      autoClose: 3000,
      closeOnClick: true
    });
  }, [error, errorMessage]);

  useEffect(() => {
    const totalPages = Math.ceil(quantity / DEFAULT_PAGE_SIZE);
    if (activePage > totalPages && totalPages > 0) {
      setActivePage(totalPages);
    }
  }, [quantity]);

  const handleSelectAllItems = async () => {
    try {
      const response = await IgnoredWordsService.getById(ignoredWords);
      const wordsList = response.data.data?.words || response.data?.words || [];
      selectAllItems(
        wordsList.map((word, index) => ({ id: `${index}-${word}`, word }))
      );
    } catch (error) {
      toast.error('Erro ao selecionar todas as palavras');
    }
  };

  const handleDelete = (ids) => {
    const wordsToDelete = ids.map((id) => {
      const parts = id.split('-');
      if (parts.length < 2) {
        throw new Error(`Formato de ID inválido: ${id}`);
      }
      return parts.slice(1).join('-');
    });

    IgnoredWordsService.getById(ignoredWords)
      .then((response) => {
        const currentWords =
          response.data.data?.words.map((word) => word.original) ||
          response.data?.words.map((word) => word.original) ||
          [];

        if (!Array.isArray(currentWords)) {
          throw new Error(
            'Formato de resposta inválido: lista de palavras não encontrada'
          );
        }

        const updatedWords = currentWords.filter(
          (word) => !wordsToDelete.includes(word)
        );

        return IgnoredWordsService.update(ignoredWords, {
          ...response.data.data,
          words: updatedWords.map((word) => ({
            original: word,
            sanitized: null
          }))
        });
      })
      .then((updateResponse) => {
        if (updateResponse.status === 200) {
          unselectAllItems();
          refetch();
          toast.success('Palavra(s) removida(s) com sucesso!');
        } else {
          throw new Error(
            updateResponse.data.message || 'Erro ao atualizar palavras'
          );
        }
      })
      .catch((error) => {
        toast.error(
          `Erro ao remover palavra(s): ${
            error.response?.data?.message || 'Erro desconhecido'
          }`
        );
      })
      .finally(() => {
        setIsConfirmationModalOpen(false);
      });
  };

  return (
    <>
      <DefaultModal
        show={isModalOpen}
        onModalToggle={onModalToggle}
        title="Lista de Palavras Ignoradas"
        showCloseButton={false}
        modalContainerClasses="modal-xl"
        centered={false}
        {...props}
      >
        <>
          <Row>
            <Col className="d-flex flex-row justify-content-between">
              <div className="d-flex flex-column justify-content-between">
                {isAllRowsSelected && quantity > selectedRows.length && (
                  <>
                    <span>
                      Todas <strong>{selectedRows.length}</strong> palavras
                      nesta página estão selecionadas
                    </span>
                    <div className="small">
                      <Button
                        outline={true}
                        size="sm"
                        color=""
                        onClick={handleSelectAllItems}
                      >
                        <i className="fas fa-check"></i> Selecionar todas (
                        {quantity})
                      </Button>
                    </div>
                  </>
                )}
                {isAllItemsSelected && (
                  <>
                    <span>
                      Todas <strong>{selectedRows.length}</strong> palavras
                      nesta lista estão selecionadas
                    </span>
                    <div className="small">
                      <Button
                        outline={true}
                        size="sm"
                        color=""
                        onClick={unselectAllItems}
                      >
                        <i className="fas fa-undo"></i> Desfazer seleção
                      </Button>
                    </div>
                  </>
                )}
                {selectedRows.length > 0 && (
                  <div className="small">
                    <Button
                      outline={true}
                      size="sm"
                      color=""
                      onClick={() => {
                        setResourceId(selectedRows.map((row) => row.id));
                        setIsConfirmationModalOpen(true);
                      }}
                    >
                      <i className="fas fa-trash"></i> Excluir Selecionadas (
                      {selectedRows.length})
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <ReactButton
                  btnColor="confirmation"
                  type="submit"
                  size="sm"
                  onClick={() => {
                    setIsAddWordsModalOpen(true);
                  }}
                >
                  + Adicionar Palavra(s)
                </ReactButton>
              </div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <i className="fas fa-search" />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  size="md"
                  placeholder="Buscar por palavra"
                  type="text"
                  defaultValue=""
                  onChange={(e) => debouncedSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <ToolkitProvider
                data={data}
                keyField="id"
                columns={[
                  {
                    dataField: 'word',
                    text: 'Palavras',
                    sort: true,
                    style: individual_column_style,
                    headerStyle: individual_header_style
                  },
                  {
                    dataField: 'delete',
                    style: icon_column_style,
                    headerStyle: individual_icon_header_style,
                    text: 'Deletar',
                    formatter: (_, row) => (
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          setResourceId([row.id]);
                          setIsConfirmationModalOpen(true);
                        }}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    )
                  }
                ]}
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    {loading ? (
                      <Row className="justify-content-md-center">
                        <ReactLoader />
                      </Row>
                    ) : (
                      <BootstrapTable
                        {...props.baseProps}
                        selectRow={{
                          mode: 'checkbox',
                          selected: selectedRows.map((r) => r.id),
                          onSelect: (row, isSelected) =>
                            select(row, isSelected, data),
                          onSelectAll: selectAllRows,
                          selectColumnStyle: individual_header_style
                        }}
                        bootstrap4={true}
                        bordered={false}
                        classes="align-items-center"
                        noDataIndication="Nenhuma palavra encontrada"
                      />
                    )}
                  </div>
                )}
              </ToolkitProvider>
              {!!quantity && quantity > DEFAULT_PAGE_SIZE && (
                <Pagination
                  activePage={activePage}
                  onChange={(pageNumber) => {
                    setActivePage(pageNumber);
                    unselectAllItems();
                  }}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col style={{ maxWidth: '8rem' }}>
              <ReactButton
                btnColor="confirmation"
                type="submit"
                size="sm"
                onClick={() => onModalToggle(false)}
                block
              >
                OK
              </ReactButton>
            </Col>
          </Row>
        </>
      </DefaultModal>
      <ModalConfirmation
        isModalOpen={isConfirmationModalOpen}
        onModalToggle={setIsConfirmationModalOpen}
        unmountOnClose={true}
        idResource={resourceId}
        deleteResource={handleDelete}
        message={'Tem certeza que deseja excluir esta(s) palavra(s)?'}
      />

      <AddWordsModal
        isModalOpen={isAddWordsModalOpen}
        onModalToggle={setIsAddWordsModalOpen}
        unmountOnClose={true}
        wordsListId={ignoredWords}
        refetch={refetch}
        validateWords={(words) => {
          if (!words || words.length === 0)
            return 'Pelo menos uma palavra é obrigatória!';
          return (
            words.every((word) => word.value.trim() !== '') ||
            'Palavras vazias não são permitidas!'
          );
        }}
        errorFormMessage={(message) => (
          <span className="text-danger">{message}</span>
        )}
      />
    </>
  );
};

export default ListModal;
